import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, makeStyles, Paper, Button,
  Checkbox, FormControlLabel, Divider,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import EditIcon from '@material-ui/icons/Edit';
import GetAppIcon from '@material-ui/icons/GetApp';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useHistory, useParams } from 'react-router-dom';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DEVIATION_DATA, PLANNED_CC_DATA } from './manufacturingTabData';

const useStyles = makeStyles(() => ({
  root: { fontFamily: 'inherit', height: 'calc(100vh - 100px)', display: 'flex', flexDirection: 'column' },
  backBar: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '4px 0 8px', borderBottom: '1px solid #e0e0e0', marginBottom: '8px',
    flexShrink: 0,
  },
  backBtn: { textTransform: 'none', fontSize: '0.88rem', color: '#1976d2', padding: '2px 8px', cursor: 'pointer' },
  reviewTitle: { fontSize: '1.1rem', fontWeight: 700, color: '#1a1a2e' },
  infoBar: {
    backgroundColor: '#EEF4FB', borderRadius: '6px', padding: '12px 24px',
    display: 'flex', gap: '40px', marginBottom: '8px', flexWrap: 'wrap',
    flexShrink: 0,
    width: 'fit-content',
    margin: '0 auto 8px',
  },
  infoBarLabel: { fontSize: '0.75rem', fontWeight: 700, color: '#111', marginBottom: '4px' },
  infoBarValue: { fontSize: '0.92rem', color: '#444', fontWeight: 400 },
  splitPane: {
    display: 'flex', gap: '16px', flex: 1, minHeight: 0, alignItems: 'stretch',
  },
  leftPane: { flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column' },
  leftHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', flexShrink: 0 },
  leftTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' },
  btnEdit: {
    textTransform: 'none', fontSize: '0.82rem', marginRight: '8px',
    backgroundColor: '#1976d2', color: '#fff',
    '&:hover': { backgroundColor: '#1565c0' },
  },
  btnUnedited: {
    textTransform: 'none', fontSize: '0.82rem',
    backgroundColor: '#888', color: '#fff',
    '&:hover': { backgroundColor: '#777' },
  },
  formTable: { width: '100%', borderCollapse: 'collapse' },
  formRow: { '& td': { fontSize: '0.88rem', padding: '7px 10px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' } },
  formCheckCell: { width: '28px', verticalAlign: 'top !important', paddingTop: '9px !important' },
  formLabel: { color: '#555', fontWeight: 500, width: '34%', fontSize: '0.88rem', paddingRight: '8px' },
  formValue: { color: '#222', fontSize: '0.88rem', flex: 1 },
  formBadge: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    borderRadius: '4px', padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700, color: '#fff',
    minWidth: '30px', whiteSpace: 'nowrap',
  },
  badgeGreen: { backgroundColor: '#4CAF50' },
  badgeOrange: { backgroundColor: '#FF8F00' },
  badgeRed: { backgroundColor: '#d32f2f' },
  legendRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  legendLabel: { fontSize: '0.8rem', color: '#555', fontWeight: 500 },
  legendItem: { display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.8rem' },
  legendBox: { width: '16px', height: '16px', borderRadius: '2px' },
  bottomBox: {
    backgroundColor: '#f5f5f5', borderRadius: '4px', padding: '10px 14px',
    marginTop: '10px', flexShrink: 0,
  },
  noteText: { fontSize: '0.78rem', color: '#777', marginTop: '4px', fontStyle: 'italic' },
  btnReinitiate: {
    textTransform: 'none', fontSize: '0.82rem', marginTop: '10px',
    backgroundColor: '#444', color: '#fff',
    width: 'fit-content', borderRadius: '20px',
    '&:hover': { backgroundColor: '#333' },
  },
  rightPane: { flex: '1 1 50%', minWidth: 0, display: 'flex', flexDirection: 'column' },
  pdfHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px', flexShrink: 0 },
  pdfTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' },
  btnDownload: {
    textTransform: 'none', fontSize: '0.82rem',
    backgroundColor: '#EC6535', color: '#fff',
    '&:hover': { backgroundColor: '#d4551f' },
  },
  pdfToolbar: {
    display: 'flex', alignItems: 'center', gap: '6px', backgroundColor: '#555',
    padding: '6px 10px', borderRadius: '4px 4px 0 0', color: '#fff', flexShrink: 0,
  },
  pdfToolbarIcon: { fontSize: '1.1rem', cursor: 'pointer', color: '#eee', '&:hover': { color: '#fff' } },
  pdfToolbarText: { fontSize: '0.82rem', color: '#ddd' },
  pdfBody: {
    border: '1px solid #ccc', borderTop: 'none', borderRadius: '0 0 4px 4px',
    padding: '16px', backgroundColor: '#fff', flex: 1, minHeight: 0, overflowY: 'auto',
  },
  pdfCompany: { fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '2px' },
  pdfLogo: { fontSize: '0.85rem', color: '#1976d2', fontWeight: 600 },
  pdfRef: { fontSize: '0.78rem', color: '#555', marginBottom: '8px' },
  pdfDocTitle: { fontSize: '0.95rem', fontWeight: 700, textAlign: 'center', marginBottom: '12px', color: '#1a1a2e' },
  pdfSection: { fontSize: '0.85rem', fontWeight: 700, color: '#222', marginBottom: '4px', marginTop: '8px' },
  pdfFieldRow: { display: 'flex', gap: '8px', marginBottom: '3px', alignItems: 'flex-start' },
  pdfFieldLabel: { fontSize: '0.82rem', color: '#444', minWidth: '150px' },
  pdfFieldColon: { fontSize: '0.82rem', color: '#444', marginRight: '4px' },
  pdfFieldValue: { fontSize: '0.82rem', color: '#222' },
  pdfHighlight: { backgroundColor: '#ffeb3b', padding: '0 2px', borderRadius: '2px', fontSize: '0.82rem', fontWeight: 600 },
  pdfHighlightRed: { border: '1px solid #d32f2f', padding: '0 4px', borderRadius: '2px', fontSize: '0.82rem', color: '#d32f2f' },
  pdfDesc: { fontSize: '0.82rem', color: '#333', lineHeight: 1.5, marginTop: '4px' },
}));

const DEVIATION_FIELDS = [
  { label: 'DR No.', value: 'QE-2026-005', score: 93 },
  { label: 'Date', value: '12/23/2025 16:04', score: 92 },
  {
    label: 'Description of Deviation',
    value: "Product: 403213 / Paracetamol 50 ml vial Stage C Batch No 400682 is under execution at Production Block-11 through BPR (MBPR-QE-005, Version no. 00). At Op. No. 44, during the filtration in Agitated Nutsche Filter Dryer #DNFD25-1, material passing was observed in the filtered mother liquors (MLs). This was observed by Mr. Grandhe Sriram, Production Shift In-charge on date 21-Dec-2025.",
    score: 94,
  },
  { label: 'Corrective Action Proposed', value: 'NA', score: 96 },
  {
    label: 'Preventive Action Proposed',
    value: 'The filter cloth fixing checklist for Agitated Nutsche Filter Dryer (ANFD) (reference No. F-PR-113) shall be revised to incorporate the following checkpoints:\na) A new point shall be incorporated as "if any abnormality is observed and notification is provided during the filtration on the ANFD bottom plate.\nb) A new point shall be incorporated as "If any abnormality is observed and notification is performed during filter cloth fixing verification, all checkpoints shall be repeated by using a new checklist after the verification".',
    score: 96,
  },
  { label: 'Status', value: 'Closed', score: 98 },
  { label: 'Nature of Deviation', value: 'Major', score: 91 },
  { label: 'Deviation Type', value: 'Process', score: 98 },
  { label: 'If yes, has deviation notified?', value: 'Yes', score: 93 },
];

const CC_FIELDS = [
  { label: 'CC No.', value: 'CMO-CC-2026-020', score: 93 },
  { label: 'Existing Number', value: 'MBPR-QE-001 Version: 00', score: 92 },
  { label: 'Revised Number', value: 'MBPR-QE-001 Version: 01', score: 94 },
  {
    label: 'Change From',
    value: 'N-(4-hydroxyphenyl) Acetamide Stage C) approved Batch hProduction Record is available with following details:\n\n1) At Operation number 39 filter cloth usage instruction available as "use 1.0 – 5.0 micron PP/ Nylon filter cloth".\n2) At operation number 44, 45, 48, 49, 51, 52, 56, 60, 61 and 64 filtration followed by washing of the wet material operational instructions available.\n3) At operation number 51, 52, 60 and 61 slurry wash to the wet material operational instructions available in single lot provision.\n4) At operation number 68 recommended drying time available as "2-3 hours".',
    score: 94,
  },
  {
    label: 'Proposed Change',
    value: 'Proposed to revise 4-(Aminomethyl)-6-bromophthalazin-1(2H)-one (MIR-006432) (BSQ stage-C) Batch Production Record with following changes:\n\n1) At Operation number 39 filter cloth usage instruction to be changed as "use 1.0 micron PP filter cloth".\n2) At operation number 44, 45, 48, 49, 50, 51, 52, 56, 60, 61 and 64 filtration followed by washing of the wet material operational instructions to be elaborated as "if product is observed in MLs, inform to PE/PR&D for further recommendation and based on recommendation, collect it in clean and dry container / ML catch pot/collection receiver and recirculate the obtained filter ML\'s through ANFD until clear filter ML\'s were obtained and then continue filtration. If recirculation is to be performed, ensure the containers (Used for filter ml\'s collection) are clean and dry prior to recirculation."\n3) At operation number 51, 52, 60 and 61 slurry wash to the wet material operational instructions to be provided in three lot provision.\n4) At operation number 68 Recommended drying time to be changed as "4-5 hours".\n5) At operation number 2 instruction to be incorporated as "Ensure ANFD ML catch pot is clean, dry and free from previous product. Note: Record the catch pot cleaning details in Equipment Cleaning Record table".',
    score: 94,
  },
  {
    label: 'Reason for Proposed Change',
    value: '1) Based on previous batch experience the material is in fine nature so recommended to fix the 1 micron PP filter cloth.\n2) Based on previous batch learning (Ref. Batch number: DBSQC25001), material passage was observed during filtration so as a precautionary measure operational instruction elaborated to reduce the material loss in the filter MLs.\n3) Total slurry wash volume is higher than the overall holding capacity of ANFD (including wet material) hence, to accommodate the bigger batch size proposed to split the slurry wash volume in three equal lot and perform the ANFD material slurry washes in lot wise (three lots).\n4) Recommended drying time increased to reduce lumps formation.\n5) Proposed to perform recirculation of the filter MLs into ANFD through catch pot hence, to ensure cleanliness of the catch pot operation was incorporated.',
    score: 97,
  },
  { label: 'Status', value: 'Closed', score: 92 },
];

const getBadgeClass = (score, classes) => {
  if (score === null || score === undefined) return null;
  if (score >= 90) return classes.badgeGreen;
  if (score >= 80) return classes.badgeOrange;
  return classes.badgeRed;
};

// ── Resolve the clicked Deviation/CC record (and its batch) from the real data ──
const fv = (v) => (v == null || v === '' ? 'NA' : String(v));
const dateOnly = (ts) => (ts ? String(ts).split(/[ T]/)[0] : 'NA');

// batch -> product display + CMO/MFG site (VV = OXB Durham, Trastuzumab = Greenville)
const productInfo = (batch) => {
  if (batch === 'TZB2401-DS') return { cmoMfg: 'CMO - Greenville', product: '575432 / Trastuzumab', batch };
  return {
    cmoMfg: 'OXB - Durham',
    product: batch === '2890-OXB-D-1' ? '2441 / Anti-CD19 CAR LVV Drug Substance' : '2440 / CD19 CAR-LVV',
    batch: batch || 'NA',
  };
};

const findRecord = (dataMap, match) => {
  for (const batch of Object.keys(dataMap || {})) {
    const rec = (dataMap[batch] || []).find(match);
    if (rec) return { rec, batch };
  }
  return { rec: null, batch: null };
};

const resolveDoc = (isDeviation, docId, stateRow) => {
  const id = docId || (stateRow && stateRow.id);
  if (!id) return { rec: null, batch: null };
  return isDeviation
    ? findRecord(DEVIATION_DATA, (d) => d.deviationNumber === id)
    : findRecord(PLANNED_CC_DATA, (c) => c.cmoCCNumber === id || c.sponsorCCNumber === id);
};

const buildDeviationFields = (d) => (d ? [
  { label: 'DR No.', value: fv(d.deviationNumber), score: 96 },
  { label: 'Date', value: dateOnly(d.reportingDate), score: 92 },
  { label: 'Description of Deviation', value: fv(d.initialDescription), score: 94 },
  { label: 'Corrective Action Proposed', value: fv(d.correctiveAction && d.correctiveAction.text), score: 95 },
  { label: 'Preventive Action Proposed', value: fv(d.preventiveAction && d.preventiveAction.text), score: 96 },
  { label: 'Status', value: fv(d.deviationStatus), score: 98 },
  { label: 'Nature of Deviation', value: fv(d.deviationCategory), score: 91 },
  { label: 'Deviation Type', value: fv(d.deviationType), score: 97 },
  { label: 'If yes, has deviation notified?', value: fv(d.informedSponsor), score: 93 },
] : []);

const buildCCFields = (c) => (c ? [
  { label: 'CC No.', value: fv(c.cmoCCNumber || c.sponsorCCNumber), score: 96 },
  { label: 'Existing Number', value: fv(c.initialDocNumber), score: 92 },
  { label: 'Revised Number', value: fv(c.finalDocNumber), score: 94 },
  { label: 'Change From', value: fv(c.changeFrom), score: 93 },
  { label: 'Proposed Change', value: fv(c.changeTo), score: 94 },
  { label: 'Reason for Proposed Change', value: fv(c.reasonsForChange), score: 96 },
  { label: 'Status', value: fv(c.cmoCCStatus || c.sponsorCCStatus), score: 95 },
] : []);

const ReviewDocumentPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const { docType, docId } = useParams();
  const [applyAll, setApplyAll] = useState(false);

  const isDeviation = docType === 'deviation';
  const stateRow = history.location && history.location.state && history.location.state.row;
  const { rec, batch } = resolveDoc(isDeviation, docId, stateRow);
  const pinfo = productInfo(batch);
  const title = isDeviation ? 'Deviation Report' : 'Change Controls Report';
  const formFields = isDeviation ? buildDeviationFields(rec) : buildCCFields(rec);
  const formTitle = isDeviation ? 'Deviation Report Data' : 'Change Controls Report Data';
  const pdfContentRef = useRef(null);

  const handleDownload = useCallback(async () => {
    const el = pdfContentRef.current;
    if (!el) return;
    const canvas = await html2canvas(el, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
    let position = 0;
    const pageHeight = pdf.internal.pageSize.getHeight();
    // Handle multi-page if content is tall
    while (position < pdfHeight) {
      if (position > 0) pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, -position, pdfWidth, pdfHeight);
      position += pageHeight;
    }
    const fileName = isDeviation ? 'Deviation_Report.pdf' : 'Change_Controls_Report.pdf';
    pdf.save(fileName);
  }, [isDeviation]);

  return (
    <Box className={classes.root}>
      {/* Back bar */}
      <Box className={classes.backBar}>
        <Button className={classes.backBtn} startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}>
          Back
        </Button>
        <Typography className={classes.reviewTitle}>{title}</Typography>
      </Box>

      {/* Info bar - centered */}
      <Box className={classes.infoBar}>
        {[
          { label: 'CMO - MFG', value: pinfo.cmoMfg },
          { label: 'Product ID / Product Description', value: pinfo.product },
          { label: 'CMO Work Order Number', value: 'CMO12345' },
          { label: 'Batch Number', value: pinfo.batch },
        ].map(item => (
          <Box key={item.label}>
            <Typography className={classes.infoBarLabel}>{item.label}</Typography>
            <Typography className={classes.infoBarValue}>{item.value}</Typography>
          </Box>
        ))}
      </Box>

      {/* Split pane */}
      <Box className={classes.splitPane}>
        {/* LEFT: Form */}
        <Box className={classes.leftPane}>
          <Box className={classes.leftHeader}>
            <Typography className={classes.leftTitle}>{formTitle}</Typography>
            <Box>
              <Button variant="contained" size="small" className={classes.btnEdit} startIcon={<EditIcon style={{ fontSize: '0.9rem' }} />}>
                Edit
              </Button>
              <Button variant="contained" size="small" className={classes.btnUnedited}>
                Show Unedited Version
              </Button>
            </Box>
          </Box>

          <Paper variant="outlined" style={{ borderRadius: '4px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <table className={classes.formTable}>
              <tbody>
                {formFields.map((field, i) => (
                  <tr key={i} className={classes.formRow}>
                    <td className={classes.formCheckCell}>
                      <CheckBoxIcon style={{ fontSize: '1.2rem', color: '#EC6535' }} />
                    </td>
                    <td className={classes.formLabel}>{field.label}</td>
                    <td className={classes.formValue}>
                      <Typography style={{ fontSize: '0.88rem', color: '#222', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{field.value}</Typography>
                    </td>
                    <td style={{ width: '40px', textAlign: 'right', paddingLeft: '4px' }}>
                      {field.score != null && (
                        <span className={`${classes.formBadge} ${getBadgeClass(field.score, classes)}`}>
                          {field.score}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>

          {/* Confidence legend — outside grey box */}
          <Box className={classes.legendRow} style={{ marginTop: '10px', flexShrink: 0 }}>
            <Typography className={classes.legendLabel} style={{ fontStyle: 'italic' }}>Confidence Levels Legend:</Typography>
            {[
              { label: '>90', color: '#4CAF50' },
              { label: '80 to 90', color: '#FF8F00' },
              { label: '<80', color: '#d32f2f' },
            ].map(l => (
              <Box
                key={l.label}
                style={{
                  backgroundColor: l.color, color: '#fff', borderRadius: '4px',
                  padding: '2px 10px', fontSize: '0.78rem', fontWeight: 600,
                }}
              >
                {l.label}
              </Box>
            ))}
          </Box>

          {/* Apply feature + Note — in grey box */}
          <Box className={classes.bottomBox}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={applyAll}
                  onChange={(e) => setApplyAll(e.target.checked)}
                  style={{ padding: '4px 8px', color: '#EC6535' }}
                />
              }
              label={<span style={{ fontSize: '0.88rem' }}>Apply feature to all fields</span>}
            />
            <Typography className={classes.noteText}>
              Note - This will enable editing of the high confidence level fields, highlighted in green colors.
            </Typography>
          </Box>
          {!isDeviation && (
            <Button variant="contained" size="small" className={classes.btnReinitiate}>
              Update Values And Reinitiate Process Flow Changes
            </Button>
          )}
        </Box>

        {/* RIGHT: PDF View */}
        <Box className={classes.rightPane}>
          <Box className={classes.pdfHeader}>
            <Typography className={classes.pdfTitle}>PDF View</Typography>
            <Button variant="contained" size="small" className={classes.btnDownload} startIcon={<GetAppIcon style={{ fontSize: '1rem' }} />} onClick={handleDownload}>
              Download
            </Button>
          </Box>

          <Box className={classes.pdfToolbar}>
            <NavigateBeforeIcon className={classes.pdfToolbarIcon} />
            <NavigateNextIcon className={classes.pdfToolbarIcon} />
            <Typography className={classes.pdfToolbarText}>1 of 1</Typography>
            <Box style={{ flex: 1 }} />
            <ZoomOutIcon className={classes.pdfToolbarIcon} />
            <ZoomInIcon className={classes.pdfToolbarIcon} />
          </Box>

          <Box className={classes.pdfBody} ref={pdfContentRef}>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' }}>
              <Box>
                <Typography className={classes.pdfCompany}>Oxford Biomedica</Typography>
                <Typography style={{ fontSize: '0.72rem', color: '#888' }}>Corporate</Typography>
              </Box>
              <Box style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Box style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#1976d2' }} />
                <Typography className={classes.pdfLogo}>Oxford Biomedica</Typography>
              </Box>
            </Box>

            <Typography className={classes.pdfRef}>
              Reference SOP No. &amp; Title: {isDeviation ? '99-03, Handling of deviations' : '99-13, Change Control'}
            </Typography>
            <Typography className={classes.pdfDocTitle}>
              {isDeviation ? 'DEVIATION REPORT' : 'PROCESS/ EQUIPMENT / SITE / SPEC CHANGE REQUEST'}
            </Typography>
            <Divider style={{ marginBottom: '8px' }} />

            {isDeviation ? (
              <>
                {/* DR No. and Status row */}
                <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span className={classes.pdfHighlightRed}>DR No. :  {fv(rec && rec.deviationNumber)}</span>
                  <span className={classes.pdfHighlightRed}>Status :  {fv(rec && rec.deviationStatus)}</span>
                </Box>

                <Typography className={classes.pdfSection}>1. 0 Deviation Details :</Typography>
                {[
                  { label: 'DR Category', value: 'Production' },
                  { label: 'Product Name', value: pinfo.product },
                  { label: 'Batch No(s)', value: pinfo.batch },
                ].map(f => (
                  <Box key={f.label} className={classes.pdfFieldRow}>
                    <Typography className={classes.pdfFieldLabel}>{f.label}</Typography>
                    <Typography className={classes.pdfFieldColon}>:</Typography>
                    <Typography className={classes.pdfFieldValue}>{f.value}</Typography>
                  </Box>
                ))}

                <Typography className={classes.pdfSection}>2.0 Deviation Observed in :</Typography>
                <Box className={classes.pdfFieldRow}>
                  <Typography className={classes.pdfFieldLabel}>Deviation Type</Typography>
                  <Typography className={classes.pdfFieldColon}>:</Typography>
                  <span className={classes.pdfHighlightRed}>{fv(rec && rec.deviationType)}</span>
                </Box>

                <Typography className={classes.pdfSection}>3.0 Description of Deviation :</Typography>
                <Typography className={classes.pdfDesc}>{fv(rec && rec.initialDescription)}</Typography>

                <Typography className={classes.pdfSection}>4.0 Corrective Action :</Typography>
                <Typography className={classes.pdfDesc}>{fv(rec && rec.correctiveAction && rec.correctiveAction.text)}</Typography>

                <Typography className={classes.pdfSection}>5.0 Preventive Action :</Typography>
                <Typography className={classes.pdfDesc}>{fv(rec && rec.preventiveAction && rec.preventiveAction.text)}</Typography>
              </>
            ) : (
              <>
                {/* CC No. and Status row */}
                <Box style={{ display: 'flex', gap: '24px', marginBottom: '8px' }}>
                  <Typography className={classes.pdfFieldValue}><strong>CC No.</strong> :  {fv(rec && (rec.cmoCCNumber || rec.sponsorCCNumber))}</Typography>
                  <Typography className={classes.pdfFieldValue}><strong>Status</strong> :  {fv(rec && (rec.cmoCCStatus || rec.sponsorCCStatus))}</Typography>
                </Box>

                <Typography className={classes.pdfSection}>1.0  Change Request Details :</Typography>
                {[
                  { label: 'CC Category', value: 'Product / Process Change' },
                  { label: 'Product name', value: pinfo.product },
                ].map(f => (
                  <Box key={f.label} className={classes.pdfFieldRow}>
                    <Typography className={classes.pdfFieldLabel}>{f.label}</Typography>
                    <Typography className={classes.pdfFieldColon}>:</Typography>
                    <Typography className={classes.pdfFieldValue}>{f.value}</Typography>
                  </Box>
                ))}

                <Typography className={classes.pdfSection}>2.0  Document / Version :</Typography>
                <Box style={{ marginTop: '4px' }}>
                  <Typography style={{ fontSize: '0.82rem', color: '#222', marginBottom: '3px' }}>
                    <strong>Existing Number</strong> :  {fv(rec && rec.initialDocNumber)}
                  </Typography>
                  <Typography style={{ fontSize: '0.82rem', color: '#222' }}>
                    <strong>Revised Number</strong> :  {fv(rec && rec.finalDocNumber)}
                  </Typography>
                </Box>

                <Typography className={classes.pdfSection}>3.0  Reason for Change :</Typography>
                <Typography className={classes.pdfDesc}>{fv(rec && rec.reasonsForChange)}</Typography>
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ReviewDocumentPage;
